import styled from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';
import {
  MMediaStackAspectRatio,
  MMediaStackImageDirection,
  MMediaStackImageLayout,
} from '../../molecules/MMediaStack/MMediaStack.types';
import { OEditorialHighlightContentAlignment, OEditorialHighlightOverlay } from './OEditorialHighlight.types';
import {
  getAspectRatioCssValue,
  isOneOnePlusTwoThreeAspectRatio,
  resolveMMediaStackAspectRatio,
} from './OEditorialHighlight.utils';

/** Figma S: 18px side inset (576–767). */
const MOBILE_SIDE_INSET = toRem(18);
/** Figma L: 119.5px viewport side margin. */
const LARGE_SIDE_INSET = toRem(119.5);
/** Figma XL: 150px viewport side margin; inner max 1290px in 1440 artboard. */
const XL_SIDE_INSET = toRem(150);
const XL_INNER_MAX_WIDTH = toRem(1290);
const TEXT_BLOCK_FULL_CSS_VALUE = `display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;`;

export const contentJustify = ($contentAlignment: OEditorialHighlightContentAlignment) => {
  if ($contentAlignment === OEditorialHighlightContentAlignment.Left) {
    return 'flex-start';
  }
  if ($contentAlignment === OEditorialHighlightContentAlignment.Right) {
    return 'flex-end';
  }
  return 'center';
};

export const contentAlignItems = ($contentAlignment: OEditorialHighlightContentAlignment) => {
  if ($contentAlignment === OEditorialHighlightContentAlignment.Centered) {
    return 'center';
  }
  if ($contentAlignment === OEditorialHighlightContentAlignment.Right) {
    return 'flex-end';
  }
  return 'flex-start';
};

// ─── S (<768): stacked, centered copy, 18px inset, bg + scrim when overlay is set ───
// ─── M (768–991): 768×512 (3:2), 24px margins, two columns, 86px column gap ───
// ─── L (992–1199): 1200×675 (16:9), 119.5px margins, 107px column gap ───
// ─── XL (1200+): 1440×810 (16:9), 150px margins, 1290 inner width, 121px column gap ───
export const Container = styled.div<{ $container?: string }>`
  position: relative;
  overflow: hidden;
  background-color: var(--Color-set-brand-bg-color, #fff);
  color: var(--Color-set-brand-fg-color, #1c1c1c);

  &.m-container-fullbleed {
    width: 100vw !important;
  }

  /* Figma S / mobile (<768): 18px side inset, 64px top & bottom padding */
  padding-inline: ${MOBILE_SIDE_INSET};

  @media ${baseVariables.mediaQuery.md} {
    aspect-ratio: 3 / 2;
    padding-block: 0;
    padding-inline: var(--General-spacer-04, 1.5rem);
  }

  @media ${baseVariables.mediaQuery.lg} {
    aspect-ratio: 16 / 9;
    padding-inline: ${LARGE_SIDE_INSET};
  }

  @media ${baseVariables.mediaQuery.xl} {
    padding-inline: ${XL_SIDE_INSET};
    display: flex;
    justify-content: center;
  }
  [data-component-name='a-web-ui-kit-AImage'] {
    height: 100% !important;
  }
`;

export const BackgroundMedia = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  [data-testid='web-ui-kit-AImage'],
  picture,
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const Wrapper = styled.div<{ $imageLayout: MMediaStackImageLayout }>`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media ${baseVariables.mediaQuery.md} {
    flex-direction: row;
    align-items: stretch;
    justify-content: flex-start;
    gap: ${toRem(86)};
    height: 100%;
  }

  @media ${baseVariables.mediaQuery.lg} {
    gap: ${toRem(107)};
  }

  @media ${baseVariables.mediaQuery.xl} {
    gap: ${toRem(121)};
    max-width: ${XL_INNER_MAX_WIDTH};
    margin: 0 auto;
  }
`;

export const ContentSection = styled.div<{
  $contentAlignment: OEditorialHighlightContentAlignment;
  $imageLayout: MMediaStackImageLayout;
}>`
  position: relative;
  display: flex;
  flex: 1 1;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding-block-start: ${toRem(64)};
  background-color: unset !important;
  text-align: ${props =>
    props.$contentAlignment === OEditorialHighlightContentAlignment.Centered ? 'center' : 'start'};

  @media ${baseVariables.mediaQuery.md} {
    flex-direction: row;
    width: auto;
    order: ${props => (props.$imageLayout === MMediaStackImageLayout.Reverse ? 2 : 1)};
    align-items: center;
    justify-content: ${props => contentJustify(props.$contentAlignment)};
    padding-inline: 0;
  }
`;

export const ScrimGradientWrapper = styled.div<{
  $overlay?: OEditorialHighlightOverlay;
  $imageLayout: MMediaStackImageLayout;
}>`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: visible;
  /* Matches _colors.scss scrim tokens used by AScrimGradient */
  background: ${props =>
    props.$overlay === OEditorialHighlightOverlay.Dark
      ? baseVariables.gradient['baseGradient05']
      : baseVariables.gradient['baseGradient01']};
  transform: scaleY(-1);
  --t-color-scrim-100: ${props =>
    props.$overlay === OEditorialHighlightOverlay.Light ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.70)'};

  @media ${baseVariables.mediaQuery.md} {
    background: ${props =>
      props.$overlay === OEditorialHighlightOverlay.Dark
        ? baseVariables.gradient['baseGradient07']
        : baseVariables.gradient['baseGradient03']};
    transform: ${props => (props.$imageLayout === MMediaStackImageLayout.Reverse ? 'scaleX(-1)' : 'scaleY(-1)')};
  }
`;

export const ContentWrapper = styled.div<{
  $contentAlignment: OEditorialHighlightContentAlignment;
}>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-inline-size: 100%;
  align-items: ${props => contentAlignItems(props.$contentAlignment)};

  [data-component-name='m-web-ui-kit-MTextBlockFull'] {
    [class*='EyebrowWrapper'] {
      padding-block-end: ${toRem(8)};
      margin-block-end: 0;

      [data-component-name='a-web-ui-kit-AEyebrow'] {
        padding-block-end: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    [class*='HeaderWrapper'] {
      padding-block-end: ${toRem(12)};
      margin-block-end: 0;

      [data-component-name='a-web-ui-kit-AHeader'] {
        padding-block-end: 0;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        -webkit-line-clamp: 4;
      }

      @media ${baseVariables.mediaQuery.lg} {
        [data-component-name='a-web-ui-kit-AHeader'] {
          -webkit-line-clamp: 3;
        }
      }
    }

    [class*='SubheaderWrapper'] {
      padding-block-end: ${toRem(4)};
      margin-block-end: 0;

      [data-component-name='a-web-ui-kit-ASubheader'] {
        padding-block-end: 0;
        ${TEXT_BLOCK_FULL_CSS_VALUE}
        -webkit-line-clamp: 3;
      }

      @media ${baseVariables.mediaQuery.md} {
        [data-component-name='a-web-ui-kit-ASubheader'] {
          -webkit-line-clamp: 2;
        }
      }
    }

    [class*='BodyWrapper'] {
      padding-block-end: ${toRem(4)};

      [data-component-name='a-web-ui-kit-ABody'] {
        padding-block-end: 0;
        ${TEXT_BLOCK_FULL_CSS_VALUE}
        -webkit-line-clamp: 5;
      }

      @media ${baseVariables.mediaQuery.md} {
        [data-component-name='a-web-ui-kit-ABody'] {
          -webkit-line-clamp: 4;
        }
      }

      @media ${baseVariables.mediaQuery.lg} {
        [data-component-name='a-web-ui-kit-ABody'] {
          -webkit-line-clamp: 3;
        }
      }
    }
  }

  @media ${baseVariables.mediaQuery.md} {
    max-inline-size: ${toRem(600)};
  }
`;

export const MediaSection = styled.div<{
  $imageLayout: MMediaStackImageLayout;
  $mediaStackAspectRatio: MMediaStackAspectRatio;
  $mediaStackImageDirection: MMediaStackImageDirection;
}>`
  --Color-set-brand-bg-color: transparent;
  flex: 1 1;
  width: 100%;

  /* Mobile: height from mMediaStack.aspectRatio + imageDirection (AEM / JSON) */
  > [data-component-name='m-web-ui-kit-MMediaStack'] {
    width: 100%;
    height: auto;

    [data-component-name='m-web-ui-kit-MMediaSelector'] {
      border-radius: 0 !important;
    }
  }

  /* Single-ratio stacks: sync secondary m-media-border + img with mMediaStack.aspectRatio (AEM often omits border AR). */
  ${props => {
    if (isOneOnePlusTwoThreeAspectRatio(props.$mediaStackAspectRatio)) {
      return '';
    }
    const stackAr = getAspectRatioCssValue(resolveMMediaStackAspectRatio(props.$mediaStackAspectRatio));
    return `
    [data-component-name='m-web-ui-kit-MMediaBorder'] {
      aspect-ratio: ${stackAr} !important;
      border-radius: 0 !important;
      [data-component-name='m-web-ui-kit-MMediaSelector'] {
        border-radius: 0 !important;
        [data-component-name='m-video-background'] {
          border-radius: 0 !important;
        }
        [data-component-name='m-web-ui-kit-MMediaImage'] {
          border-radius: 0 !important;
        }
        [data-component-name="m-web-ui-kit-MVideoThumbnail"] {
          border-radius: 0 !important;
        }
      }
    }
    [data-component-name='m-web-ui-kit-MMediaBorder'] picture,
    [data-component-name='m-web-ui-kit-MMediaBorder'] img {
      aspect-ratio: ${stackAr} !important;
      height: 100%;
    }
  `;
  }}

  @media ${baseVariables.mediaQuery.md} {
    order: ${props => (props.$imageLayout === MMediaStackImageLayout.Reverse ? 1 : 2)};

    > [data-component-name='m-web-ui-kit-MMediaStack'] {
      height: 100%;
    }
  }
`;

export const TextBlockWrapper = styled.div<{ $contentAlignment: OEditorialHighlightContentAlignment }>`
  width: 100%;

  /* Figma S: center m-text-block-full when stacked on mobile */
  [data-component-name='m-web-ui-kit-MTextBlockFull'] {
    align-items: center;
    text-align: ${props =>
      props.$contentAlignment === OEditorialHighlightContentAlignment.Centered ? 'center' : 'start'};
  }

  @media ${baseVariables.mediaQuery.md} {
    [data-component-name='m-web-ui-kit-MTextBlockFull'] {
      align-items: ${props =>
        props.$contentAlignment === OEditorialHighlightContentAlignment.Centered
          ? 'center'
          : props.$contentAlignment === OEditorialHighlightContentAlignment.Right
          ? 'flex-end'
          : 'flex-start'};
    }
  }
`;

export const ButtonWrapper = styled.div<{
  $contentAlignment: OEditorialHighlightContentAlignment;
  $overlay: OEditorialHighlightOverlay;
}>`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-block-start: ${toRem(16)};

  @media ${baseVariables.mediaQuery.md} {
    justify-content: ${props =>
      props.$contentAlignment === OEditorialHighlightContentAlignment.Centered
        ? 'center'
        : props.$contentAlignment === OEditorialHighlightContentAlignment.Right
        ? 'flex-end'
        : 'flex-start'};
  }

  [data-component-name='m-web-ui-kit-MButton'] {
    border-radius: 0 !important;
  }
`;
